import { ref } from 'vue';

// Interface para los datos de impuestos
interface Tax {
  code: number;
  enumName: string;
  value: number;
  taxType: number;
  description: string;
  active: boolean;
  id: number;
  status: number;
}

// Estado reactivo para almacenar los datos de impuestos
const taxesData = ref<Tax[]>([]);
const isInitialized = ref<boolean>(false);

/**
 * Establece los datos de impuestos en el estado
 * @param taxes Array de impuestos desde el backend
 */
function setTaxesData(taxes: Tax[]): void {
  taxesData.value = taxes || [];
  isInitialized.value = true;
}

/**
 * Obtiene todos los impuestos
 * @returns Array de todos los impuestos
 */
function getTaxes(): Tax[] {
  return taxesData.value;
}

/**
 * Obtiene solo los impuestos activos
 * @returns Array de impuestos activos
 */
function getActiveTaxes(): Tax[] {
  return taxesData.value.filter((tax) => tax.active);
}

/**
 * Obtiene el valor de un impuesto por su código (solo si está activo)
 * @param code Código del impuesto
 * @returns Valor del impuesto o 0 si no se encuentra o no está activo
 */
function getTaxValueByCode(code: number): number {
  const tax = taxesData.value.find((tax) => tax.code === code);
  return tax ? tax.value : 0;
}

/**
 * Obtiene un impuesto completo por su código
 * @param code Código del impuesto
 * @returns Objeto Tax o null si no se encuentra
 */
function getTaxByCode(code: number): Tax | null {
  return taxesData.value.find((tax) => tax.code === code) || null;
}

/**
 * Verifica si existe un impuesto activo con el código dado
 * @param code Código del impuesto
 * @returns true si existe y está activo, false en caso contrario
 */
function taxExists(code: number): boolean {
  return taxesData.value.some((tax) => tax.code === code && tax.active);
}

/**
 * Obtiene el array formateado para dropdowns con TODOS los impuestos (activos e inactivos)
 * @returns Array en formato { value, label } con todos los impuestos
 */
function getTaxesArray(): Array<{ value: number; label: string }> {
  return taxesData.value.map((tax) => ({
    value: tax.code,
    label: `enums.iva.${tax.enumName.toLowerCase()}`,
  }));
}

/**
 * Obtiene solo los impuestos activos formateados para dropdowns
 * @returns Array en formato { value, label } solo con impuestos activos
 */
function getActiveTaxesArray(): Array<{ value: number; label: string }> {
  return taxesData.value
    .filter((tax) => tax.active)
    .map((tax) => ({
      value: tax.code,
      label: `enums.iva.${tax.enumName.toLowerCase()}`,
    }));
}

/**
 * Verifica si los datos de impuestos han sido inicializados
 * @returns true si están inicializados, false en caso contrario
 */
function hasTaxesData(): boolean {
  return isInitialized.value && taxesData.value.length > 0;
}

/**
 * Limpia los datos de impuestos (para logout)
 */
function clearTaxesData(): void {
  taxesData.value = [];
  isInitialized.value = false;
}

export default {
  // Estado
  taxesData,
  isInitialized,

  // Funciones de gestión
  setTaxesData,
  getTaxes,
  getActiveTaxes,
  getTaxValueByCode,
  getTaxByCode,
  taxExists,
  getTaxesArray,
  getActiveTaxesArray,
  hasTaxesData,
  clearTaxesData,
};

export type { Tax };
